'use client';
import React from 'react';
import Link from 'next/link';

interface AuthLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle: string;
}

export default function AuthLayout({ children, title, subtitle }: AuthLayoutProps) {
  return (
    <div className="min-h-screen bg-[#FAFAFA] flex font-sans-landing">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gray-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute top-0 left-0 w-full h-full"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-600/20 to-transparent" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 group">
            <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center group-hover:bg-violet-500 transition-colors">
              <span className="text-white font-bold text-2xl">W</span>
            </div>
            <span className="text-3xl font-bold text-white">Waitlistly</span>
          </Link>

          {/* Main Content */}
          <div className="max-w-md">
            <h1 className="text-4xl font-bold text-white leading-tight mb-6">
              Validate your ideas with beautiful waitlist pages
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed">
              Create stunning landing pages in minutes. Collect emails, track interest, and know if your idea has legs—before you build.
            </p>

            {/* Stats */}
            <div className="mt-10 flex gap-8">
              <div>
                <div className="text-3xl font-bold text-violet-400">5 min</div>
                <div className="text-sm text-gray-500 mt-1">To go live</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-violet-400">Free</div>
                <div className="text-sm text-gray-500 mt-1">To build</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-violet-400">&infin;</div>
                <div className="text-sm text-gray-500 mt-1">Pages</div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} Waitlistly. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-start lg:items-center justify-center px-6 py-8 lg:py-12 min-h-screen overflow-y-auto">
        <div className="max-w-md w-full">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link href="/" className="inline-flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center">
                <span className="text-white font-bold text-lg">W</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">Waitlistly</span>
            </Link>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <p className="text-gray-500 mt-2">{subtitle}</p>
          </div>

          {children}
        </div>
      </div>
    </div>
  );
}
